import { ArrowLeft, Loader2, Plus, Star, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ImageUploader } from '@/components/forms/ImageUploader';
import { lenderService } from '@/services/lender.service';
import type { CreateLenderInput } from '@/types/lender';
import { EntityStatus } from '@/types/lender';

interface FormData {
  name: string;
  companyName: string;
  email: string;
  phone: string;
  profileLogo: string;
  rating: number;
  loanTypes: string[];
  websiteUrl: string;
  description: string;
  companyNmls: string;
  nmlsId: string;
  status: EntityStatus;
}

// Track display URL separately (full URL from API for display)
interface DisplayUrls {
  profileLogo?: string;
}

const initialFormData: FormData = {
  name: '',
  companyName: '',
  email: '',
  phone: '',
  profileLogo: '',
  rating: 0,
  loanTypes: [],
  websiteUrl: '',
  description: '',
  companyNmls: '',
  nmlsId: '',
  status: EntityStatus.ACTIVE,
};

const commonLoanTypes = ['FHA 203k', 'Conventional', 'VA', 'USDA', 'Jumbo', 'Bridge'];

export function LenderForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = id && id !== 'new';

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [displayUrls, setDisplayUrls] = useState<DisplayUrls>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditMode);
  const [error, setError] = useState<string | null>(null);
  const [newLoanType, setNewLoanType] = useState('');

  const fetchLender = useCallback(async () => {
    if (!id) return;

    setIsFetching(true);
    setError(null);

    try {
      const lender = await lenderService.getById(id);
      setFormData({
        name: lender.name,
        companyName: lender.companyName,
        email: lender.email || '',
        phone: lender.phone || '',
        profileLogo: lender.profileLogo || '',
        rating: lender.rating,
        loanTypes: lender.loanTypes || [],
        websiteUrl: lender.websiteUrl || '',
        description: lender.description || '',
        companyNmls: lender.companyNmls || '',
        nmlsId: lender.nmlsId || '',
        status: lender.status,
      });
      // Store the display URL (full URL from API)
      setDisplayUrls({
        profileLogo: lender.profileLogo,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lender');
    } finally {
      setIsFetching(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditMode) {
      fetchLender();
    }
  }, [isEditMode, fetchLender]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData((prev) => ({ ...prev, [name]: value.replace(/\D/g, '') }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRatingChange = (newRating: number) => {
    setFormData((prev) => ({
      ...prev,
      rating: newRating,
    }));
  };

  const handleAddLoanType = (loanType: string) => {
    if (loanType && !formData.loanTypes.includes(loanType)) {
      setFormData((prev) => ({
        ...prev,
        loanTypes: [...prev.loanTypes, loanType],
      }));
    }
    setNewLoanType('');
  };

  const handleRemoveLoanType = (loanType: string) => {
    setFormData((prev) => ({
      ...prev,
      loanTypes: prev.loanTypes.filter((t) => t !== loanType),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const input: CreateLenderInput = {
        name: formData.name,
        companyName: formData.companyName,
        email: formData.email,
        phone: formData.phone,
        rating: formData.rating,
        loanTypes: formData.loanTypes,
        status: formData.status,
      };

      // Only include optional fields if they have values
      if (formData.profileLogo) input.profileLogo = formData.profileLogo;
      if (formData.websiteUrl) input.websiteUrl = formData.websiteUrl;
      if (formData.description) input.description = formData.description;
      if (formData.companyNmls) input.companyNmls = formData.companyNmls;
      if (formData.nmlsId) input.nmlsId = formData.nmlsId;

      if (isEditMode && id) {
        await lenderService.update(id, input);
      } else {
        await lenderService.create(input);
      }

      navigate('/lenders');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save lender');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center py-[64px]">
        <div className="h-[32px] w-[32px] animate-spin rounded-full border-2 border-[#055135] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-[32px]">
      {/* Header */}
      <div className="flex items-center gap-[16px]">
        <button
          type="button"
          onClick={() => navigate('/lenders')}
          className="flex h-[40px] w-[40px] items-center justify-center rounded-[8px] text-[#646464] transition-colors hover:bg-[#f5f5f5] hover:text-[#181818]"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-[24px] font-semibold text-[#181818]">
          {isEditMode ? 'Edit Lender' : 'Add Lender'}
        </h1>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-[8px] border border-[#fca5a5] bg-[#fef2f2] p-[16px] text-[14px] text-[#b91c1c]">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-[24px]">
        <div className="overflow-hidden rounded-[12px] border border-[#e5e5e5] bg-white">
          <div className="border-b border-[#e5e5e5] bg-[#f5f5f5] px-[24px] py-[16px]">
            <h2 className="text-[16px] font-semibold text-[#181818]">Basic Information</h2>
          </div>
          <div className="space-y-[20px] p-[24px]">
            {/* Name */}
            <div className="grid grid-cols-2 gap-[20px]">
              <div>
                <label
                  htmlFor="name"
                  className="mb-[8px] block text-[14px] font-medium text-[#181818]"
                >
                  Name <span className="text-[#d4183d]">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label
                  htmlFor="companyName"
                  className="mb-[8px] block text-[14px] font-medium text-[#181818]"
                >
                  Company Name <span className="text-[#d4183d]">*</span>
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                  placeholder="ABC Lending"
                />
              </div>
            </div>

            {/* Email & Phone (Internal Use Only) */}
            <div className="grid grid-cols-2 gap-[20px]">
              <div>
                <label
                  htmlFor="email"
                  className="mb-[8px] block text-[14px] font-medium text-[#181818]"
                >
                  Email Address <span className="text-[#d4183d]">*</span>
                  <span className="ml-[8px] text-[12px] font-normal text-[#8d8c8f]">
                    (Internal use only)
                  </span>
                </label>
                <input
                  type="email"
                  pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                  title="Please enter a valid email address"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                  placeholder="john@abclending.com"
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="mb-[8px] block text-[14px] font-medium text-[#181818]"
                >
                  Phone Number <span className="text-[#d4183d]">*</span>
                  <span className="ml-[8px] text-[12px] font-normal text-[#8d8c8f]">
                    (Internal use only)
                  </span>
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            {/* Loan Types */}
            <fieldset className="border-0 p-0 m-0">
              <legend className="mb-[8px] block text-[14px] font-medium text-[#181818]">
                Loan Types
              </legend>
              <div className="mb-[12px] flex flex-wrap gap-[8px]">
                {formData.loanTypes.map((type) => (
                  <span
                    key={type}
                    className="inline-flex items-center gap-[4px] rounded-[6px] bg-[#d5eee9] px-[12px] py-[6px] text-[13px] font-medium text-[#055135]"
                  >
                    {type}
                    <button
                      type="button"
                      onClick={() => handleRemoveLoanType(type)}
                      className="ml-[4px] text-[#055135] hover:text-[#044128]"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-[8px]">
                <select
                  value=""
                  onChange={(e) => handleAddLoanType(e.target.value)}
                  className="h-[40px] flex-1 rounded-[8px] border border-[#e5e5e5] bg-white px-[12px] text-[14px] text-[#181818] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                >
                  <option value="">Select loan type...</option>
                  {commonLoanTypes
                    .filter((type) => !formData.loanTypes.includes(type))
                    .map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                </select>
                <div className="flex gap-[8px]">
                  <input
                    type="text"
                    value={newLoanType}
                    onChange={(e) => setNewLoanType(e.target.value)}
                    placeholder="Custom type"
                    className="h-[40px] w-[160px] rounded-[8px] border border-[#e5e5e5] bg-white px-[12px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                  />
                  <button
                    type="button"
                    onClick={() => handleAddLoanType(newLoanType)}
                    disabled={!newLoanType}
                    className="flex h-[40px] w-[40px] items-center justify-center rounded-[8px] bg-[#055135] text-white transition-colors hover:bg-[#044128] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>
            </fieldset>

            {/* Website URL */}
            <div>
              <label
                htmlFor="websiteUrl"
                className="mb-[8px] block text-[14px] font-medium text-[#181818]"
              >
                Website URL
              </label>
              <input
                type="url"
                id="websiteUrl"
                name="websiteUrl"
                value={formData.websiteUrl}
                onChange={handleChange}
                className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                placeholder="https://example.com"
              />
            </div>

            {/* Company NMLS & Individual NMLS ID */}
            <div className="grid grid-cols-2 gap-[20px]">
              <div>
                <label
                  htmlFor="companyNmls"
                  className="mb-[8px] block text-[14px] font-medium text-[#181818]"
                >
                  Company NMLS
                </label>
                <input
                  type="text"
                  id="companyNmls"
                  name="companyNmls"
                  value={formData.companyNmls}
                  onChange={handleChange}
                  className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                  placeholder="123456"
                />
              </div>
              <div>
                <label
                  htmlFor="nmlsId"
                  className="mb-[8px] block text-[14px] font-medium text-[#181818]"
                >
                  Individual NMLS ID
                </label>
                <input
                  type="text"
                  id="nmlsId"
                  name="nmlsId"
                  value={formData.nmlsId}
                  onChange={handleChange}
                  className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                  placeholder="789012"
                />
              </div>
            </div>

            {/* Rating & Status */}
            <div className="grid grid-cols-2 gap-[20px]">
              <div>
                <fieldset className="border-0 p-0 m-0">
                  <legend className="mb-[8px] block text-[14px] font-medium text-[#181818]">
                    Rating
                  </legend>
                  <div className="flex items-center gap-[8px]">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleRatingChange(star)}
                        className="p-[4px] transition-transform hover:scale-110"
                      >
                        <Star
                          size={24}
                          className={
                            star <= formData.rating
                              ? 'fill-[#fbbf24] text-[#fbbf24]'
                              : 'text-[#e5e5e5] hover:text-[#fbbf24]'
                          }
                        />
                      </button>
                    ))}
                    <span className="ml-[8px] text-[14px] text-[#646464]">
                      {formData.rating.toFixed(1)}
                    </span>
                  </div>
                </fieldset>
              </div>
              <div>
                <label
                  htmlFor="status"
                  className="mb-[8px] block text-[14px] font-medium text-[#181818]"
                >
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                >
                  <option value={EntityStatus.ACTIVE}>Active</option>
                  <option value={EntityStatus.INACTIVE}>Inactive</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="description"
                className="mb-[8px] block text-[14px] font-medium text-[#181818]"
              >
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] py-[12px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                placeholder="Enter company description..."
              />
            </div>
          </div>
        </div>

        {/* Profile Logo Section */}
        <div className="overflow-hidden rounded-[12px] border border-[#e5e5e5] bg-white">
          <div className="border-b border-[#e5e5e5] bg-[#f5f5f5] px-[24px] py-[16px]">
            <h2 className="text-[16px] font-semibold text-[#181818]">Media</h2>
          </div>
          <div className="p-[24px]">
            <ImageUploader
              label="Profile Logo"
              value={formData.profileLogo || undefined}
              displayUrl={displayUrls.profileLogo}
              onChange={(key) => setFormData((prev) => ({ ...prev, profileLogo: key || '' }))}
              folder="lenders/logo"
              entityId={id}
              accept="image/*"
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end gap-[12px]">
          <button
            type="button"
            onClick={() => navigate('/lenders')}
            className="flex h-[48px] items-center gap-[8px] rounded-[8px] border border-[#e5e5e5] bg-white px-[24px] text-[14px] font-medium text-[#646464] transition-colors hover:border-[#646464] hover:text-[#181818]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex h-[48px] items-center gap-[8px] rounded-[8px] bg-[#055135] px-[24px] text-[14px] font-medium text-white transition-colors hover:bg-[#044128] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading && <Loader2 size={18} className="animate-spin" />}
            {isEditMode ? 'Update Lender' : 'Create Lender'}
          </button>
        </div>
      </form>
    </div>
  );
}
